import type { ReactNode } from "react";
import { StyleSheet, useWindowDimensions, View } from "react-native";

import {
  radii,
  shadows,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import type { Space } from "./types";

export function Card({
  children,
  danger = false,
  accent = "none",
  compact = false,
  elevated = false,
  gap,
}: {
  children: ReactNode;
  danger?: boolean;
  /** Soft left accent for metrics / emphasis without double nesting. */
  accent?: "none" | "mint" | "danger";
  compact?: boolean;
  /** Adds a subtle shadow — reserved for surfaces that float (modals). */
  elevated?: boolean;
  gap?: Space;
}) {
  const { stylesheet, gapStyles } = useThemedStyles(makeStyles);
  const resolvedAccent = danger ? "danger" : accent;
  // Phones: tighter padding so nested rows keep a usable width.
  const narrow = useWindowDimensions().width < 480;

  return (
    <View
      style={[
        stylesheet.card,
        resolvedAccent === "mint" ? stylesheet.mint : null,
        resolvedAccent === "danger" ? stylesheet.danger : null,
        elevated ? shadows.sm : null,
        narrow ? stylesheet.narrow : null,
        compact ? stylesheet.compact : null,
        gap ? gapStyles[gap] : null,
      ]}
    >
      {children}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    card: {
      padding: spacing.xl,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.card,
      backgroundColor: colors.elevated,
    },
    mint: {
      borderLeftWidth: 3,
      borderLeftColor: colors.primary,
    },
    danger: {
      borderColor: colors.border,
      borderLeftWidth: 3,
      borderLeftColor: colors.danger,
      backgroundColor: colors.elevated,
    },
    narrow: { padding: spacing.l },
    compact: { padding: spacing.l },
    gapXs: { gap: spacing.xs },
    gapSm: { gap: spacing.s },
    gapMd: { gap: spacing.m },
    gapLg: { gap: spacing.l },
    gapXl: { gap: spacing.xl },
  });
  return {
    stylesheet,
    gapStyles: {
      xs: stylesheet.gapXs,
      sm: stylesheet.gapSm,
      md: stylesheet.gapMd,
      lg: stylesheet.gapLg,
      xl: stylesheet.gapXl,
    },
  };
};
